import { Empty } from "antd";
import classNames from "classnames";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import utc from "dayjs/plugin/utc";
import "dayjs/locale/fr";
import Notifications from "entities/Notifications";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  MdNotificationsActive,
  MdOutlineChevronRight,
  MdPhoneMissed,
} from "react-icons/md";
import { useNavigate } from "react-router-dom";
import { useMutation } from "shared/hooks/useApi";
import { useNotificationBanner } from "shared/components/NotificationBanner";
import Pagination from "shared/components/Pagination";
import { NAME_SPACES } from "shared/locales/constants";
import graphql from "utils/api/graphql";
import { NOTIFICATION_TYPES } from "utils/constants";
import { formatNotificationDateAsFromNow } from "utils/helpers/notificationDate";
import { NOTIFICATION_TYPE_ACTIONS } from "utils/helpers/notificationActions";

dayjs.extend(relativeTime);
dayjs.extend(utc);

const FILTER_KEYS = ["all", "tasks", "calls", "unread"];

const NOTIFICATION_CONFIG = {
  [NOTIFICATION_TYPES.TASK_REMINDER]: {
    icon: MdNotificationsActive,
    colorClass: "notification-card--task",
    badgeLabel: "TASK_BADGE",
    actionLabel: "VIEW_TASK",
  },
  [NOTIFICATION_TYPES.MISSED_CALL]: {
    icon: MdPhoneMissed,
    colorClass: "notification-card--call",
    badgeLabel: "CALL_BADGE",
    actionLabel: "VIEW_CONTACT",
  },
};

const NotificationCard = ({
  item,
  userId,
  LIST,
  navigate,
  markAsRead,
  language,
}) => {
  const config =
    NOTIFICATION_CONFIG[item.type] ||
    NOTIFICATION_CONFIG[NOTIFICATION_TYPES.TASK_REMINDER];
  const IconComponent = config.icon;
  const isUnread = !(item.isReadBy || []).some((u) => u.id === userId);
  const isHighImportance = item.importance === "HIGH";

  const handleClick = () => {
    if (isUnread) {
      markAsRead({
        variables: {
          where: { id: item.id },
          data: {
            isReadBy: [
              ...(item.isReadBy || []).map((u) => ({ id: u.id })),
              { id: userId },
            ],
          },
        },
      });
    }
  };

  const handleAction = (e) => {
    e.stopPropagation();
    handleClick();
    const action = NOTIFICATION_TYPE_ACTIONS[item.type];
    if (typeof action === "function") {
      action(item, { navigate });
    }
  };

  const hasAction = typeof NOTIFICATION_TYPE_ACTIONS[item.type] === "function";

  return (
    <div
      className={classNames("notification-card", config.colorClass, {
        "notification-card--unread": isUnread,
        "notification-card--high": isHighImportance,
      })}
      onClick={handleClick}
    >
      <div className="notification-card__icon-wrapper">
        <IconComponent size={isHighImportance ? 28 : 22} />
      </div>

      <div className="notification-card__body">
        <div className="notification-card__header">
          <div className="notification-card__title-row">
            {isUnread && <span className="notification-card__dot" />}
            <span className="notification-card__title">
              {item.title || LIST.DEFAULT_TITLE}
            </span>
            <span
              className={classNames(
                "notification-card__badge",
                config.colorClass,
              )}
            >
              {LIST[config.badgeLabel]}
            </span>
          </div>
          <span className="notification-card__date">
            {formatNotificationDateAsFromNow(item.createdDate, language)}
          </span>
        </div>
        <p className="notification-card__content">{item.content}</p>
      </div>

      {hasAction && (
        <div className="notification-card__action" onClick={handleAction}>
          <span>{LIST[config.actionLabel]}</span>
          <MdOutlineChevronRight size={18} />
        </div>
      )}
    </div>
  );
};

const RECENT_MINUTES = 2;

const NotificationList = ({
  data,
  count,
  setValues,
  skip,
  userId,
  activeFilter,
}) => {
  const { t, i18n } = useTranslation(NAME_SPACES.PRIVATE.BROKER.NOTIFICATION);
  const LIST = t("LIST", { returnObjects: true });
  const navigate = useNavigate();
  const language = i18n.language || "fr";
  const { addBannerIfNew } = useNotificationBanner() || {};

  useEffect(() => {
    if (!addBannerIfNew || !userId || !data?.length) return;
    const cutoff = dayjs.utc().subtract(RECENT_MINUTES, "minute");
    (data || []).forEach((item) => {
      const isUnread = !(item.isReadBy || []).some(
        (u) => String(u.id) === String(userId),
      );
      if (!isUnread) return;
      const raw = item.createdDate;
      const asUtc =
        typeof raw === "string" && raw.length > 0 && !raw.endsWith("Z")
          ? `${raw}Z`
          : raw;
      const created = dayjs.utc(asUtc);
      if (created.isValid() && created.isAfter(cutoff)) {
        addBannerIfNew(item);
      }
    });
  }, [data, userId, addBannerIfNew]);

  const [markAsRead] = useMutation(graphql.mutations.UPDATE_NOTIFICATION, {
    refetchQueries: [
      "Notifications",
      { query: graphql.queries.UNREAD_NOTIFICATIONS_COUNT },
    ],
    awaitRefetchQueries: true,
  });

  const filteredData =
    activeFilter === "unread"
      ? (data || []).filter(
          (item) => !(item.isReadBy || []).some((u) => u.id === userId),
        )
      : data || [];

  if (!filteredData.length) {
    return <Empty description={LIST.NO_NOTIFICATIONS} />;
  }

  return (
    <>
      <div className="notification-list">
        {filteredData.map((item) => (
          <NotificationCard
            key={item.id}
            item={item}
            userId={userId}
            LIST={LIST}
            navigate={navigate}
            markAsRead={markAsRead}
            language={language}
          />
        ))}
      </div>
      <Pagination
        count={activeFilter === "unread" ? filteredData.length : count}
        setValues={setValues}
        skip={skip}
      />
    </>
  );
};

const View = ({
  filter,
  setValues,
  skip,
  activeFilter,
  setActiveFilter,
  userId,
}) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.BROKER.NOTIFICATION);
  const LIST = t("LIST", { returnObjects: true });

  return (
    <div className="notification-page">
      <div className="notification-page__filters">
        {FILTER_KEYS.map((key) => (
          <div
            key={key}
            className={classNames("notification-page__filter-tab", {
              "notification-page__filter-tab--active": activeFilter === key,
            })}
            onClick={() => setActiveFilter(key)}
          >
            {LIST.FILTERS[key.toUpperCase()]}
          </div>
        ))}
      </div>
      <Notifications
        View={NotificationList}
        filter={filter}
        setValues={setValues}
        skip={skip}
        activeFilter={activeFilter}
        userId={userId}
      />
    </div>
  );
};

export default View;
